import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CalendarCheck, CalendarX, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/alternative`;
const WD = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const fmt = (s) => { const [y, m, d] = s.split("-").map(Number); return `${WD[new Date(y, m - 1, d).getDay()]}, ${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}`; };

export default function AlternativePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/${token}`).then(async (r) => { const d = await r.json(); r.ok ? setData(d) : setError(d.detail); }).catch(() => setError("Verbindung fehlgeschlagen"));
  }, [token]);

  const answer = async (action) => {
    setBusy(true);
    const r = await fetch(`${API}/${token}/${action}`, { method: "POST" });
    const d = await r.json();
    r.ok ? setData(d) : setError(d.detail);
    setBusy(false);
  };

  const answered = data && data.status !== "alternative_proposed";
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 py-16">
      <Helmet><title>Terminvorschlag | Aktas Personal Fussballtraining</title><meta name="robots" content="noindex" /></Helmet>
      <div className="w-full max-w-lg bg-[#121212] border border-white/10 p-6 sm:p-8" data-testid="alternative-page">
        <h1 className="font-['Bebas_Neue'] text-4xl tracking-wide mb-1">TERMIN<span className="text-[#c1ff72]">VORSCHLAG</span></h1>
        {error && <p className="text-red-400 mt-4" data-testid="alternative-error">{error}</p>}
        {!data && !error && <Loader2 className="w-6 h-6 animate-spin text-[#c1ff72] mt-6" />}
        {data && (
          <>
            <p className="text-[#A1A1AA] mb-6">Hallo {data.customer_first_name}, {answered ? "danke für deine Rückmeldung." : "dein Wunschtermin ist leider nicht möglich. Passt dir dieser Termin?"}</p>
            <div className="border-l-4 border-[#c1ff72] bg-[#1A1A1A] p-4 mb-4" data-testid="alternative-proposed">
              <p className="text-xs uppercase tracking-widest text-[#c1ff72] mb-1">Vorgeschlagener Termin</p>
              <p className="text-lg font-semibold">{fmt(data.proposed.date)}</p>
              <p>{data.proposed.time}–{data.proposed.end_time} Uhr · {data.duration} Min · {data.training_name}</p>
              <p className="text-sm text-[#A1A1AA]">{data.training_location}</p>
            </div>
            <p className="text-sm text-[#71717A] mb-4">Dein ursprünglicher Wunsch: {fmt(data.original.date)}, {data.original.time} Uhr</p>
            {data.message && <p className="text-sm bg-[#0A0A0A] border border-white/10 p-3 mb-4">{data.message}</p>}
            {answered ? (
              <div className={`flex items-center gap-2 p-4 border ${data.answer === "accepted" ? "border-green-500/50 text-green-400" : "border-red-500/40 text-red-300"}`} data-testid="alternative-result">
                {data.answer === "accepted" ? <CalendarCheck className="w-5 h-5" /> : <CalendarX className="w-5 h-5" />}
                {data.answer === "accepted" ? "Termin angenommen und verbindlich bestätigt. Du erhältst eine Bestätigung per E-Mail." : "Vorschlag abgelehnt. Du kannst jederzeit einen neuen Termin buchen."}
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <Button disabled={busy} onClick={() => answer("accept")} className="flex-1 bg-[#c1ff72] text-black hover:bg-[#a8e65c] font-semibold py-6 rounded-none" data-testid="alternative-accept">Termin annehmen</Button>
                <Button disabled={busy} variant="outline" onClick={() => answer("decline")} className="flex-1 border-white/20 text-white py-6 rounded-none" data-testid="alternative-decline">Ablehnen</Button>
              </div>
            )}
            {answered && data.answer !== "accepted" && <Link to="/buchen" className="block mt-4 text-[#c1ff72] hover:underline text-sm">Neuen Termin buchen</Link>}
          </>
        )}
      </div>
    </div>
  );
}
